












import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { dwspayService } from "@/helpers/dwspayService";

@Component
export default class DwspayPayment extends Vue {
  @Prop(String) private clientSecret!: string;
  @Prop(String) private locale?: string;

  private isInitialized: boolean = false;
  private paymentElementValid: boolean = false;

  async mounted() {
    if (this.clientSecret && !this.isInitialized) {
      await this.initializePaymentElement();
    }

    // Subscribe to payment element changes
    dwspayService.onPaymentElementChange((event: any) => {
      this.paymentElementValid = event.complete && !event.error;
      this.$emit("paymentOK", this.paymentElementValid);
      
      const displayError = document.getElementById("dwspay-card-errors");
      if (event.error) {
        if (displayError) {
          displayError.textContent = event.error.message;
        }
      } else {
        if (displayError) {
          displayError.textContent = "";
        }
      }
    });

    // Subscribe to ready event
    dwspayService.onPaymentElementReady((ready: boolean) => {
      if (ready) {
        this.$emit("paymentOK", false); // Initially not valid
      }
    });
  }

  @Watch('clientSecret')
  async onClientSecretChanged(newVal: string, oldVal: string) {
    if (newVal && newVal !== oldVal) {
      // If already initialized, destroy and recreate with new clientSecret
      if (this.isInitialized) {
        console.debug('DWSPay Component: clientSecret changed, recreating Payment Element');
        dwspayService.destroyPaymentElement();
        this.isInitialized = false;
        await this.initializePaymentElement();
      } else {
        // Initialize if we have a new client secret and haven't initialized yet
        await this.initializePaymentElement();
      }
    }
  }

  async initializePaymentElement() {
    if (!this.clientSecret || this.isInitialized) {
      return;
    }

    try {
      // Wait a bit to ensure the DOM element is available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if the container element exists
      const container = document.getElementById('dwspay-payment-element');
      if (!container) {
        console.warn('DWSPay: Container element not found, retrying...');
        // Retry after a short delay
        setTimeout(async () => {
          if (!this.isInitialized && this.clientSecret) {
            await this.initializePaymentElement();
          }
        }, 500);
        return;
      }

      await dwspayService.initPaymentElement(this.clientSecret, 'dwspay-payment-element', this.locale);
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing Payment Element:', error);
      // Reset initialization flag to allow retry
      this.isInitialized = false;
    }
  }

  beforeDestroy() {
    dwspayService.destroyPaymentElement();
    this.isInitialized = false;
  }

  async dwspayPaymentHandler(reservationId?: string, isReservation: boolean = true) {
    if (!this.clientSecret) {
      throw new Error('Client secret not available');
    }

    const isReady = await dwspayService.waitForPaymentElementReady(5000);
    if (!isReady) {
      throw new Error('Payment Element initialization timeout');
    }

    const returnUrlParams: any = {};
    if (reservationId) {
      returnUrlParams.reservation_id = reservationId;
      returnUrlParams.is_reservation = isReservation ? 'true' : 'false';
    }

    const result = await dwspayService.confirmPayment(this.clientSecret, returnUrlParams);

    if (result.error) {
      throw new Error(result.error.message || 'Payment confirmation failed');
    }

    const paymentStatus = (result.paymentIntent && result.paymentIntent.status) || 'unknown';
    if (paymentStatus === 'requires_payment_method') {
      throw new Error('Payment method confirmation failed');
    }

    return result;
  }
}
